/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useEffect, Fragment, memo, useCallback, useMemo, useRef } from 'react';
import Style from './../css/PaymentsAndDisbursements.module.css';
import { CheckCircleOutlineTwoTone, ErrorOutlineTwoTone, Delete } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import {
  Button,
  TableCell,
  IconButton,
  Input,
  Select,
  FormControl,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { LoanPaymentRowType } from '../types/LoanPaymentRowType';
import type { QidType } from '../types/QidType';
import useGetLoan from '../common/hooks/useGetLoan';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  dateToEpoch,
  formatMoney,
  getTimestampAsDate,
  getDateValue,
  formatSecondsTimestampAsDateInput,
  phoneNumberFormatter,
} from '../common/UtilityFunctions';
import dayjs from 'dayjs';
import useGetPayeeInfo from '../common/hooks/useGetPayeeInfo';
import useInsCompInfo from '../common/hooks/useInsCompInfo';
import useMiscPayeeInfo from '../common/hooks/useMiscPayeeInfo';
import axiosCall from '../services/axios';
import ClickAwayListener from '../components/ClickAwayListener';
import useAbortController from '../common/hooks/useAbortController';
import {
  ASSESSMENT_TRANSACTIONS,
  CHECK_NUMBER_TRANSACTIONS,
  SALE_TRANSFER_TRANSACTIONS,
  REASON_TRANSACTIONS,
  MORTGAGE_PAYMENT_TRANSACTIONS,
  CORPORATE_ADVANCE_TRANSACTIONS,
  ESCROW_TRANSACTIONS,
  FEE_CNT_TRANSACTIONS,
  FEE_NO_TRANSACTIONS,
  IMPOUND_TRANSACTIONS,
  INTEREST_TRANSACTIONS,
  LATE_CHARGE_TRANSACTIONS,
  MULTIPLE_DESCRIPTION_TRANSACTIONS,
  PAYEE2_TRANSACTIONS,
  PAYEE3_TRANSACTIONS,
  PAYEE_TRANSACTIONS,
  PAYMENT_AMOUNT_TRANSACTIONS,
  PAYMENT_RECEIVED_DATE_TRANSACTIONS,
  PRINCIPAL_CURTAIL_TRANSACTIONS,
  PRINCIPAL_TRANSACTIONS,
  SUFFIX_TRANSACTIONS,
  PIF_FORECLOSURE_STATUSES,
  SUSPENSE_TRANSACTIONS,
  UNIT_NO_TRANSACTIONS,
  TAX_DISBURSEMENT_CODES,
  HAZARD_INSURANCE_CODES,
  MORTGAGE_INSURANCE_PREMIUM_CODES,
  HOA_DISBURSEMENT_CODES,
  GROUND_RENT_DISBURSEMENT_CODES,
  PARCEL_LINE_BATCH_TYPES,
  LOAN_PAYMENT_CODES,
  ROW_TOTAL_TRANSACTIONS,
  TR_TYPE_TRANSACTIONS,
  EXCESS_FUNDS_TRANSACTIONS,
  SUBSIDY_TRANSACTIONS,
} from '../contracts/constants';
import useLoanServicing from '../common/hooks/useLoanServicing';
import useCorpAdvanceFees from '../common/hooks/useCorpAdvanceFees';
import useAssessmentFees from '../common/hooks/useAssessmentFees';
import useGetServicingFees from '../common/hooks/useGetServicingFees';
import useLoanSubsidy from '../common/hooks/useLoanSubsidy';
import useQidList from '../common/hooks/useQidList';
import type { InsCompResponse, MiscPayeeInfoResponse } from '../common/queryFunctions';

/** Batch types that use parcel-line impounds and TR categories (TX uses assessor payee; HA/GR use HOA/Ground Rent accruals-style payee). */
const isParcelDisbursementBatchType = (code: string): boolean =>
  (PARCEL_LINE_BATCH_TYPES as readonly string[]).includes(code);

const isHoaOrGroundRentParcelBatch = (code: string): boolean => code === 'HA' || code === 'GR';

/** Matches HOA / Ground Rent accruals tables (`HoaAccruals` / `GroundRentAccruals`). */
function impoundPaymentStatusLabel(impound: {
  servPremium: number;
  servAmtPaid: number;
  servSupPremium: number;
  servSupAmtPaid: number;
}): string {
  if (impound.servPremium <= impound.servAmtPaid && impound.servSupPremium <= impound.servSupAmtPaid) {
    return 'FULLY PAID';
  }
  if (impound.servAmtPaid > 0 || impound.servSupAmtPaid > 0) {
    return 'PARTLY PAID';
  }
  return 'NOT PAID';
}

function formatHoaGrPayeeLine(
  impound: { insuranceComp?: string; payeeNumber?: string },
  insList: InsCompResponse[],
  miscList: MiscPayeeInfoResponse[],
): string {
  const normComp = impound.insuranceComp?.replace(/-/g, '') || '';
  const payeeFromComp = insList.find((p) => p.compCode === normComp);
  const miscInfo = miscList.find((p) => p.payeeNumber === impound.payeeNumber);
  if (payeeFromComp) {
    return `${payeeFromComp.name1} ${phoneNumberFormatter(
      payeeFromComp.primaryContactPhone,
      payeeFromComp.primaryContactPhoneExtension,
    )}`.trim();
  }
  if (miscInfo) {
    return `${miscInfo.nameOne} ${phoneNumberFormatter(miscInfo.phoneNo, '')}`.trim();
  }
  return '';
}

/** Count inclusive payment months between subsidy start and end dates (epoch seconds). */
function countSubsidyPaymentMonths(startDate: number, endDate: number): number {
  if (!startDate || !endDate || endDate < startDate) return 0;
  const start = new Date(startDate * 1000);
  const end = new Date(endDate * 1000);
  let count = 0;
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (current <= endMonth) {
    count++;
    current.setMonth(current.getMonth() + 1);
  }
  return count;
}

/** Returns true when amount is a positive even multiple of monthlyAmount (compared in cents). */
function isEvenMultipleOfMonthlyAmount(amount: number, monthlyAmount: number): boolean {
  if (!amount || amount <= 0) return false;
  if (!monthlyAmount || monthlyAmount <= 0) return amount > 0;
  const amountCents = Math.round(amount * 100);
  const monthlyCents = Math.round(monthlyAmount * 100);
  return monthlyCents > 0 && amountCents % monthlyCents === 0;
}

/** Trim master posting codes; whitespace-only supplemental falls back to standard (backend sometimes returns `" "`). */
function effectiveImpoundMasterTrCodes(impoundMasterData: {
  standardPmtMcdType?: string;
  supplementalPmtMcdType?: string;
}) {
  const standardTrCode = String(impoundMasterData.standardPmtMcdType ?? '').trim();
  const supplementalRaw = String(impoundMasterData.supplementalPmtMcdType ?? '').trim();
  const supplementalTrCode = supplementalRaw || standardTrCode;
  return { standardTrCode, supplementalRaw, supplementalTrCode };
}

type loanPaymentRowProps = {
  removeRow: any;
  updateRow: any;
  onPostRow: (row: LoanPaymentRowType) => void;
  /** Row id currently being posted, if any — disables actions on all rows while set. */
  postingRowId?: number | null;
  index: number;
  row: LoanPaymentRowType;
  selectedTransType: string;
  suffixQidList: QidType[];
  transactionQidList: QidType[];
  token: string;
  /** When true (bulk "Apply to all" is checked in LoanPaymentTable), row-level Payment Received Date is read-only. */
  disablePaymentReceivedDate?: boolean;
  /** SQC-566: LoanPaymentTable aggregates (+1/-1) to show LoadingModal while saved impound lines hydrate after retrieve batch. */
  adjustSavedImpoundHydrationLoading?: (delta: number) => void;
};

const LoanPaymentRow = memo((props: loanPaymentRowProps) => {
  const { get } = useAbortController();
  // use a temporary value to avoid nulls as the user enters a loan number
  const [tempLoanNumber, setTempLoanNumber] = useState(props.row.loan);

  // options for the description field of the given row
  const [descriptionOptions, setDescriptionOptions] = useState([]);

  // the mortgage payment amount for applicable rows
  const [mortgagePayment, setMortgagePayment] = useState(0);
  const [excessFunds, setExcessFunds] = useState(0);

  // loan data
  const [loanError, setLoanError] = useState(false);
  const { loan, loanErrorMessages, loadingLoan } = useGetLoan(props.row.loan);
  const { loanServicing } = useLoanServicing('', props.row.loan);

  // pull the payee info based on this row's assessor code
  const { payeeInfo } = useGetPayeeInfo(props.row.assessorCode);

  // the impounds to be shown for TAX/HAZ/MIP transactions (parcels: TAX, servArray: HAZ, MIP)
  const [parcels, setParcels] = useState([]);
  const [servArray, setServArray] = useState([]);

  // a list of all existing impounds on the loan
  const [completeLoanImpoundList, setCompleteLoanImpoundList] = useState([]);

  // a list of filtered impound types based on whether the user has selected TAX, HAZ, or MIP
  const [filteredLoanImpoundList, setFilteredLoanImpoundList] = useState([]);

  // the list of impounds on a loan for the filtered impound types
  const [impoundList, setImpoundList] = useState([]);

  // the impound selected by the user from the "impound" dropdown column
  const [selectedImpound, setSelectedImpound] = useState(null);

  // the available transaction codes for this row. This can be affected by the transaction type selected, impound selected, etc.
  const [availableTransactions, setAvailableTransactions] = useState([]);

  const hoaGrCompCodes = useMemo(() => {
    const s = new Set<string>();
    if (!isHoaOrGroundRentParcelBatch(props.selectedTransType)) return s;
    parcels.forEach((p: { insuranceComp?: string }) => {
      if (p.insuranceComp) s.add(p.insuranceComp.replace(/-/g, ''));
    });
    return s;
  }, [parcels, props.selectedTransType]);

  const hoaGrPayeeNumbers = useMemo(() => {
    const s = new Set<string>();
    if (!isHoaOrGroundRentParcelBatch(props.selectedTransType)) return s;
    parcels.forEach((p: { payeeNumber?: string }) => {
      if (p.payeeNumber) s.add(p.payeeNumber);
    });
    return s;
  }, [parcels, props.selectedTransType]);

  const insCompQueries = useInsCompInfo(hoaGrCompCodes, props.token ?? '', 'paymentsParcelLineInsComp');
  const miscPayeeQueries = useMiscPayeeInfo(hoaGrPayeeNumbers, props.token ?? '', 'paymentsParcelLineMiscPayee');

  const hoaGrInsCompanies = useMemo(
    () => (insCompQueries.data ?? []).filter((row): row is InsCompResponse => row != null),
    [insCompQueries.data],
  );
  const hoaGrMiscPayees = useMemo(
    () => (miscPayeeQueries.data ?? []).filter((row): row is MiscPayeeInfoResponse => row != null),
    [miscPayeeQueries.data],
  );

  const parcelLinePayeeDisplay = useMemo(() => {
    if (!isHoaOrGroundRentParcelBatch(props.selectedTransType) || !selectedImpound) return '';
    return formatHoaGrPayeeLine(selectedImpound, hoaGrInsCompanies, hoaGrMiscPayees);
  }, [props.selectedTransType, selectedImpound, hoaGrInsCompanies, hoaGrMiscPayees]);

  /** SQC-566: Saved batch row restoring impound UI (loan entered + impound type already on the row). */
  const savedImpoundHydrationNeeded = useMemo(
    () =>
      IMPOUND_TRANSACTIONS.includes(props.selectedTransType) &&
      props.row.loan?.length === 12 &&
      Boolean(props.row.impType),
    [props.selectedTransType, props.row.loan, props.row.impType],
  );

  const savedImpoundHydrationModalPendingRef = useRef(false);
  /** SQC-566: Prevents clearing the modal when parcel effect runs before policy writes impoundList for this hydration. */
  const awaitingSavedHydrationParcelBuildRef = useRef(false);

  const finishSavedHydrationModalFromParcelEffect = useCallback(() => {
    if (!savedImpoundHydrationModalPendingRef.current || !awaitingSavedHydrationParcelBuildRef.current) return;
    awaitingSavedHydrationParcelBuildRef.current = false;
    props.adjustSavedImpoundHydrationLoading?.(-1);
    savedImpoundHydrationModalPendingRef.current = false;
  }, [props.adjustSavedImpoundHydrationLoading]);

  useEffect(() => {
    return () => {
      if (savedImpoundHydrationModalPendingRef.current && props.adjustSavedImpoundHydrationLoading) {
        props.adjustSavedImpoundHydrationLoading(-1);
        savedImpoundHydrationModalPendingRef.current = false;
        awaitingSavedHydrationParcelBuildRef.current = false;
      }
    };
  }, [props.adjustSavedImpoundHydrationLoading]);

  useEffect(() => {
    if (
      !savedImpoundHydrationNeeded &&
      savedImpoundHydrationModalPendingRef.current &&
      props.adjustSavedImpoundHydrationLoading
    ) {
      props.adjustSavedImpoundHydrationLoading(-1);
      savedImpoundHydrationModalPendingRef.current = false;
      awaitingSavedHydrationParcelBuildRef.current = false;
    }
  }, [savedImpoundHydrationNeeded, props.adjustSavedImpoundHydrationLoading]);

  // pull the list of "reason" options from QID
  const { qid: reasonList } = useQidList('MASK_CD.363');

  // errors thrown by the backend when trying to save/post transactions
  const [transactionSaveErrors, setTransactionSaveErrors] = useState([]);
  const [transactionPostErrors, setTransactionPostErrors] = useState([]);

  // a boolean value for whether or not a given field is required
  const [feeNoRequired, setFeeNoRequired] = useState(false);
  const [feeCntRequired, setFeeCntRequired] = useState(false);

  // a boolean value to disable a given field
  const [disableFeeNo, setDisableFeeNo] = useState(false);
  const [disableFeeCnt, setDisableFeeCnt] = useState(false);
  const [disableSuffix, setDisableSuffix] = useState(false);
  const [disablePrincipalCurtail, setDisablePrincipalCurtail] = useState(false);
  const [disableEscrow, setDisableEscrow] = useState(false);
  const [disableLateCharges, setDisableLateCharges] = useState(false);
  const [disableCorporateAdvance, setDisableCorporateAdvance] = useState(false);
  const [disableAssessment, setDisableAssessment] = useState(false);
  const [disableSuspense, setDisableSuspense] = useState(false);
  const [disableTransactionCode, setDisableTransactionCode] = useState(false);
  const [disableTrType, setDisableTrType] = useState(false);

  // a boolean value to track whether the payment value should be positive/negative
  const [paymentPositiveValue, setPaymentPositiveValue] = useState(true);

  // A reference to the Payment Amount input
  const paymentAmountRef = useRef(null);

  // For HAZ transactions, there are different transaction "types" available
  const [trType, setTrType] = useState('');
  const [availableTrTypes, setAvailableTrTypes] = useState([]);

  // pull the fee numbers associated with corporate advance fees
  const { corpAdvanceFeeNumbers } = useCorpAdvanceFees();

  // pull the fee numbers associated with assessment fees
  const { assessmentFeeNumbers } = useAssessmentFees();

  const isSubsidyTransaction = SUBSIDY_TRANSACTIONS.includes(props.selectedTransType);
  const { loanSubsidy, loadingLoanSubsidy } = useLoanSubsidy('/list', {
    loanNumber: props.row.loan,
    enabled: isSubsidyTransaction && props.row.loan.length === 12,
  });

  const subsidies = useMemo(() => (Array.isArray(loanSubsidy) ? loanSubsidy : []), [loanSubsidy]);
  const loadingSubsidies = loadingLoanSubsidy;

  const selectedSubsidy = useMemo(
    () => subsidies.find((subsidy) => subsidy.cnt === props.row.subsidyCnt),
    [subsidies, props.row.subsidyCnt],
  );

  const suggestedSubsidyAmount = useMemo(() => {
    if (!selectedSubsidy?.monthlyAmount) return null;
    const months = countSubsidyPaymentMonths(selectedSubsidy.startDate, selectedSubsidy.endDate);
    if (months <= 0) return null;
    return selectedSubsidy.monthlyAmount * months;
  }, [selectedSubsidy]);

  // pull the existing servicing fees for a given loan number and fee number
  const { servicingFees } = useGetServicingFees(props.row.loan, props.row.feeNo);

  // a list of fees to be shown in the dropdown
  const [feeDescriptions, setFeeDescriptions] = useState([]);

  // list of available suffixes for this row
  const [suffixList, setSuffixList] = useState([]);

  // The expected UPB on a given sale date
  const [expectedUpb, setExpectedUpb] = useState(0);

  // memoize props so they can be included as dependencies
  const rowId = useMemo(() => props.row.id, [props]);
  const updateRow = useMemo(() => props.updateRow, [props]);

  // update a field in the row state variable
  const updateField = useCallback(
    (field, value) => {
      updateRow(rowId, { [field]: value });
    },
    [updateRow, rowId],
  );

  // allow negative amounts for some transactions
  const allowNegativeAmount = useCallback(() => {
    const transType = props.selectedTransType;
    const allowNegativeAmountTransactions = [
      '11',
      '13',
      '16',
      '17',
      '21',
      '22',
      '23',
      '24',
      '2B',
      '2C',
      '2H',
      '2K',
      '2P',
      '2Z',
    ];
    if (transType && allowNegativeAmountTransactions.includes(transType)) {
      return true;
    }
    return false;
  }, [props.selectedTransType]);

  // adjust the payment amount value based on whether the expected value is positive/negative
  const updatePaymentAmount = useCallback(
    (amount) => {
      let adjustedAmount = amount.floatValue;

      // adjust the value when necessary
      if (
        !allowNegativeAmount() &&
        ((paymentPositiveValue && adjustedAmount < 0) || (!paymentPositiveValue && adjustedAmount > 0))
      ) {
        adjustedAmount = adjustedAmount * -1;
      }
      updateField('amount', adjustedAmount);
    },
    [paymentPositiveValue, updateField, allowNegativeAmount],
  );

  const handleSubsidyChange = useCallback(
    (cnt: string) => {
      const subsidy = subsidies.find((item) => item.cnt === cnt);
      if (subsidy) {
        updateRow(rowId, {
          subsidyCnt: subsidy.cnt,
          subsidyName: subsidy.subsidyName,
        });
      } else {
        updateRow(rowId, { subsidyCnt: '', subsidyName: '' });
      }
    },
    [subsidies, updateRow, rowId],
  );

  // check that appropriate fields are set for a transaction to be saved/posted
  useEffect(() => {
    let saveErrors = [];
    let postErrors = [];
    if (props.row.loan.length !== 12 || loanErrorMessages) {
      saveErrors.push('Invalid Loan Number');
    } else {
      if (!loan.fundingGLAccount) {
        saveErrors.push('fundingGLAccount not set');
      }
      if (loan.servicingState !== '1') {
        saveErrors.push('servicingState not set to active (1)');
      }
      if (loan.servStatus !== '0') {
        saveErrors.push('servStatus indicates loan is not current');
      }
    }

    if (isSubsidyTransaction) {
      if (!props.row.subsidyName || !props.row.subsidyCnt) {
        saveErrors.push('Subsidy must be selected');
      }
      const amount = parseFloat(props.row.amount);
      if (!amount || amount <= 0) {
        saveErrors.push('Amount must be greater than zero');
      } else if (selectedSubsidy && !isEvenMultipleOfMonthlyAmount(amount, selectedSubsidy.monthlyAmount)) {
        saveErrors.push(
          `Amount must be an even multiple of the monthly subsidy (${formatMoney(selectedSubsidy.monthlyAmount)})`,
        );
      }
      if (loadingSubsidies) {
        saveErrors.push('Loading subsidies for loan');
      } else if (props.row.loan.length === 12 && subsidies.length === 0) {
        saveErrors.push('No subsidies found on loan');
      }
    }

    setTransactionSaveErrors(saveErrors);
    setTransactionPostErrors(postErrors);
  }, [
    loan,
    loanErrorMessages,
    loanServicing,
    props.row.loan,
    props.row.subsidyName,
    props.row.subsidyCnt,
    props.row.amount,
    isSubsidyTransaction,
    selectedSubsidy,
    subsidies,
    loadingSubsidies,
  ]);

  // calculate and set mortgage payment amount
  useEffect(() => {
    const getMortgagePaymentAmount = async () => {
      let amount = 0;

      // use 1 month after lpi to get new mortgage payment (format: YYYYMMDD)
      const lpiDate = new Date(loan.lpi * 1000);
      lpiDate.setMonth(lpiDate.getMonth() + 1);
      const lpiPlusOneMonth = formatSecondsTimestampAsDateInput(lpiDate.getTime() / 1000).replaceAll('-', '');
      if (!props.token || !props.row.loan) return;
      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };
      const controller = get();
      const config = { signal: controller.signal };

      try {
        const url = 'live/loan/' + props.row.loan + '/impound/total/mnthly-imp-amt-at/' + lpiPlusOneMonth;

        const response = await axiosCall({ url: url, method: 'get' }, config, headers);

        if (response) {
          amount += response.mnthlyImpAmt;
        }
      } catch (error) {
        console.error('Error fetching monthly payment amount');
      }

      try {
        const url = 'live/loan/' + props.row.loan + '/payment/' + lpiPlusOneMonth;

        const response = await axiosCall({ url: url, method: 'get' }, config, headers);

        if (response) {
          amount += response.apparPi + response.escrowShortageMonthlyRepaymentAmt;
        }
      } catch (error) {
        console.error('Error fetching escrow shortage ammount');
      }

      setMortgagePayment(amount);
    };

    if (
      props.row.loan.length === 12 &&
      loan &&
      loan.lpi &&
      MORTGAGE_PAYMENT_TRANSACTIONS.includes(props.row.mcdTransType)
    ) {
      getMortgagePaymentAmount();
    }
  }, [props.row.loan, loan.lpi, get, props.token, props.row.mcdTransType]);

  useEffect(() => {
    const sumOfNonPaymentFields =
      (parseFloat(props.row.prinCurtailReqstd) || 0) +
      (parseFloat(props.row.extraEscrowReqstd) || 0) +
      (parseFloat(props.row.lateChargeReqstd) || 0) +
      (parseFloat(props.row.corpAdvanceReqstd) || 0) +
      (parseFloat(props.row.assessmentsReqstd) || 0);
    const suspense = parseFloat(props.row.suspenseReqstd);

    setExcessFunds(parseFloat(props.row.amount) - mortgagePayment - suspense - sumOfNonPaymentFields);
  }, [
    props.row.prinCurtailReqstd,
    props.row.extraEscrowReqstd,
    props.row.lateChargeReqstd,
    props.row.corpAdvanceReqstd,
    props.row.assessmentsReqstd,
    props.row.amount,
    props.row.suspenseReqstd,
    mortgagePayment,
  ]);

  // get existing impounds on the loan
  useEffect(() => {
    const getImpoundsForLoan = async () => {
      if (!props.token || !props.row.loan || props.row.loan.length !== 12) return;
      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };
      const controller = get();
      const config = { signal: controller.signal };

      try {
        const url = 'live/loan/' + props.row.loan + '/impound/list';

        const response = await axiosCall({ url: url, method: 'get' }, config, headers);

        if (response) {
          console.log(response);
          setCompleteLoanImpoundList(response);
        }
      } catch (error) {
        console.error('Error fetching impounds for loan: ' + props.row.loan, error);
      }
    };

    if (IMPOUND_TRANSACTIONS.includes(props.selectedTransType)) {
      getImpoundsForLoan();
    }
  }, [props.token, props.row.loan, props.selectedTransType, get]);

  // pull impound master data for a given impound
  const getImpoundMasterData = useCallback(
    async (impound) => {
      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };
      const controller = get();
      const config = { signal: controller.signal };
      const url = 'live/master/impound/' + impound.impoundGroup + '/' + impound.impoundType;
      try {
        const response = await axiosCall({ url: url, method: 'get' }, config, headers);
        return response;
      } catch (error) {
        console.error('Error fetching impound master data', error);
      }
    },
    [get, props.token],
  );

  // get the impounds for the loan number that has been entered using
  // the impound group and impound type specific to the transaction code
  useEffect(() => {
    const filterImpoundsByTransactionType = async () => {
      if (!props.token) return;
      let results = [];

      for await (const impound of completeLoanImpoundList) {
        try {
          console.log(impound);
          const response = await getImpoundMasterData(impound);
          console.log(response);
          if (
            props.selectedTransType === 'TX' &&
            (TAX_DISBURSEMENT_CODES.includes(response.standardPmtMcdType) ||
              TAX_DISBURSEMENT_CODES.includes(response.supplementalPmtMcdType))
          ) {
            results.push(impound);
          } else if (
            props.selectedTransType === 'HZ' &&
            (HAZARD_INSURANCE_CODES.includes(response.standardPmtMcdType) ||
              HAZARD_INSURANCE_CODES.includes(response.supplementalPmtMcdType))
          ) {
            results.push(impound);
          } else if (
            props.selectedTransType === 'MI' &&
            (MORTGAGE_INSURANCE_PREMIUM_CODES.includes(response.standardPmtMcdType) ||
              MORTGAGE_INSURANCE_PREMIUM_CODES.includes(response.supplementalPmtMcdType))
          ) {
            results.push(impound);
          } else if (
            props.selectedTransType === 'HA' &&
            (HOA_DISBURSEMENT_CODES.includes(response.standardPmtMcdType) ||
              HOA_DISBURSEMENT_CODES.includes(response.supplementalPmtMcdType))
          ) {
            results.push(impound);
          } else if (
            props.selectedTransType === 'GR' &&
            (GROUND_RENT_DISBURSEMENT_CODES.includes(response.standardPmtMcdType) ||
              GROUND_RENT_DISBURSEMENT_CODES.includes(response.supplementalPmtMcdType))
          ) {
            results.push(impound);
          }
        } catch (error) {
          console.error('Error fetching data for impound: ' + impound.impoundType, error);
        }
      }
      setFilteredLoanImpoundList(results);
    };

    if (completeLoanImpoundList) {
      filterImpoundsByTransactionType();
    }
  }, [completeLoanImpoundList, getImpoundMasterData, props.selectedTransType, props.row.loan, props.token, get]);

  // get impound policy for impound types on the given loan
  useEffect(() => {
    const getImpoundPolicyList = async () => {
      if (!props.token || !props.row.loan || !filteredLoanImpoundList) return;

      const enableHydrationTracking =
        savedImpoundHydrationNeeded && !!props.adjustSavedImpoundHydrationLoading && filteredLoanImpoundList.length > 0;

      if (enableHydrationTracking && !savedImpoundHydrationModalPendingRef.current) {
        savedImpoundHydrationModalPendingRef.current = true;
        props.adjustSavedImpoundHydrationLoading?.(1);
      }

      let results = [];
      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };
      const controller = get();
      const config = { signal: controller.signal };

      try {
        for await (const impound of filteredLoanImpoundList) {
          try {
            const url =
              'live/loan/' +
              props.row.loan +
              '/impound/' +
              impound.impoundGroup +
              '/' +
              impound.impoundType +
              '/renewalDate/list';

            const response = await axiosCall({ url: url, method: 'get' }, config, headers);

            if (response) {
              const lines = Array.isArray(response) ? response : [response];
              results.push(lines.map((line) => ({ ...line, impoundGroup: impound.impoundGroup })));
            }
          } catch (error) {
            console.error('Error fetching loan data for impound: ' + impound.impoundType, error);
          }
        }

        console.log(results);
        results = results.flatMap((res) => res);
        setImpoundList(results);
      } finally {
        if (enableHydrationTracking) {
          awaitingSavedHydrationParcelBuildRef.current = true;
        }
      }
    };

    if (filteredLoanImpoundList) {
      getImpoundPolicyList();
    }
  }, [
    props.row.loan,
    filteredLoanImpoundList,
    get,
    props.token,
    savedImpoundHydrationNeeded,
    props.adjustSavedImpoundHydrationLoading,
  ]);

  // when the impound list changes, go through and extract the items in the parcel array or serv array based on transaction type
  useEffect(() => {
    let tempParcelList = [];
    let tempServArray = [];
    let latestPaidHazardImpound = {
      renewalDte: 0,
      servicingArray: [],
    };
    let latestPaidTaxImpound = {
      renewalDte: 0,
      parcels: [],
    };
    let index = 0;
    if (props.selectedTransType === 'HZ') {
      const dateFormatConst = 'MM/DD/YYYY';
      let allServArray = [];
      let lastEntry = null;
      let idx = 0;
      impoundList.forEach((impound) => {
        if (impound.servicingArray && impound.servicingArray.length !== 0) {
          impound.servicingArray.forEach((item) => {
            allServArray.push({
              ...impound,
              index: idx,
              impType: impound.impoundType,
              unitNo: item.unitNo,
              policyNo: item.policyNo,
              amountDue: item.servArrAmountDue,
              amountPaid: item.servArrAmountPaid,
            });
            idx++;
          });
        }
      });
      allServArray.sort((imp1, imp2) => {
        if (imp1.impType !== imp2.impType) return imp1.impType - imp2.impType;
        if (imp1.policyNo !== imp2.policyNo) return imp1.policyNo - imp2.policyNo;
        if (imp1.unitNo !== imp2.unitNo) return imp1.unitNo - imp2.unitNo;
        return imp1.renewalDte - imp2.renewalDte;
      });
      allServArray.forEach((item) => {
        if (lastEntry == null) {
          lastEntry = item;
        } else {
          if (
            lastEntry.impType !== item.impType ||
            lastEntry.policyNo !== item.policyNo ||
            lastEntry.unitNo !== item.unitNo
          ) {
            tempServArray.push({
              ...lastEntry,
              index: index,
              impType: lastEntry.impType,
              unitNo: lastEntry.unitNo,
              policyNo: lastEntry.policyNo,
              amountDue: lastEntry.amountDue,
            });
            index++;
          } else if (lastEntry.amountPaid === 0.0 || item.amountPaid === 0.0) {
            // if we get here lastEntry is the same impound line as item and
            // lastEntry is the last paid entry or is unpaid.
            tempServArray.push({
              ...lastEntry,
              index: index,
              impType: lastEntry.impType,
              unitNo: lastEntry.unitNo,
              policyNo: lastEntry.policyNo,
              amountDue: lastEntry.amountDue,
            });
            index++;
          }
          lastEntry = item;
        }
      });
      if (allServArray.length !== 0) {
        tempServArray.push({
          ...lastEntry,
          index: index,
          impType: lastEntry.impType,
          unitNo: lastEntry.unitNo,
          policyNo: lastEntry.policyNo,
          amountDue: lastEntry.amountDue,
        });
        index++;
      }
    } else {
      impoundList.forEach((impound) => {
        if (isParcelDisbursementBatchType(props.selectedTransType)) {
          if (props.selectedTransType === 'TX') {
            // Tax: parcel splits and assessor-backed lines (matches tax accruals / assessor columns).
            if (impound.servPremium > 0) {
              if (impound.parcels && impound.parcels.length !== 0) {
                impound.parcels.forEach((parcel) => {
                  tempParcelList.push({
                    ...impound,
                    index: index,
                    parcelNo: parcel.parcelNo,
                    taxAmountDue: parcel.amountDue,
                    assessorCode: parcel.assessorCode,
                  });
                  index += 1;
                });
              }
            } else if (impound.servPremium === 0 && impound.servBalance > 0) {
              if (impound.renewalDte > latestPaidTaxImpound.renewalDte) {
                latestPaidTaxImpound = impound;
              }
            }
          } else {
            // HOA / Ground Rent: same data as Escrow Accruals tables — one row per policy line (not parcel-exploded).
            const hasUnpaidPremium =
              impound.servPremium > impound.servAmtPaid || impound.servSupPremium > impound.servSupAmtPaid;
            if (hasUnpaidPremium) {
              tempParcelList.push({
                ...impound,
                index: index,
                parcelNo: impound.policyNum || impound.impoundType || '',
                taxAmountDue: impound.servPremium,
                assessorCode: '',
              });
              index += 1;
            }
          }
        } else if (props.selectedTransType === 'MI') {
          if (impound.servicingArray && impound.servicingArray.length !== 0) {
            impound.servicingArray.forEach((item) => {
              tempServArray.push({
                ...impound,
                index: index,
                unitNo: item.unitNo,
                amountDue: item.servArrAmountDue,
                policyNo: item.policyNo,
              });
              index += 1;
            });
          } else {
            tempServArray.push({
              ...impound,
              index: index,
              unitNo: '',
              amountDue: impound.servPremium,
              policyNo: impound.policyNum,
            });
            index += 1;
          }
        }
        tempParcelList.forEach((impound) => {
          delete impound.parcels;
        });
      });
    }

    // Append parcel rows from the latest paid tax impound when balance-only policy lines exist (tax only).
    if (props.selectedTransType === 'TX' && latestPaidTaxImpound.parcels && latestPaidTaxImpound.parcels.length !== 0) {
      latestPaidTaxImpound.parcels.forEach((parcel) => {
        tempParcelList.push({
          ...latestPaidTaxImpound,
          index: index,
          parcelNo: parcel.parcelNo,
          taxAmountDue: parcel.amountDue,
          assessorCode: parcel.assessorCode,
        });
        index += 1;
      });
    }

    setServArray(tempServArray);

    // Resolve assessor labels for tax parcels only (HOA / GR use company + misc payee like accruals tables).
    if (props.selectedTransType === 'TX' && tempParcelList.length > 0 && props.token) {
      const uniqueCodes = [...new Set(tempParcelList.map((p) => p.assessorCode).filter(Boolean))];
      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };

      Promise.all(
        uniqueCodes.map((code) =>
          axiosCall({ url: `live/master/assessor/${code}`, method: 'get' }, {}, headers)
            .then((res) => [code, res?.nameOne || code])
            .catch(() => [code, code]),
        ),
      )
        .then((entries) => {
          const nameMap = Object.fromEntries(entries);
          setParcels(
            tempParcelList.map((p) => ({
              ...p,
              nameOne: nameMap[p.assessorCode] || p.assessorCode,
            })),
          );
        })
        .finally(() => {
          finishSavedHydrationModalFromParcelEffect();
        });
    } else {
      setParcels(tempParcelList);
      finishSavedHydrationModalFromParcelEffect();
    }
  }, [impoundList, props.selectedTransType, props.token, finishSavedHydrationModalFromParcelEffect]);

  // when a specific TAX impound type is selected to be paid, set the required fields using the impound object
  const handleSelectTaxImpoundChange = (event) => {
    const selectedIndex = event.target.value;
    const selected = parcels.find((item) => item.index === selectedIndex);
    setSelectedImpound(selected);
  };

  // when a specific HAZ or MIP impound type is selected to be paid, set the required fields using the impound object
  const handleSelectImpoundChange = (event) => {
    const selectedIndex = event.target.value;
    const selected = servArray.find((item) => item.index === selectedIndex);
    setSelectedImpound(selected);
  };

  // when the selected impound changes, update relevant fields based on impound and filter available transaction codes
  useEffect(() => {
    const updateImpoundSelection = async (impound) => {
      updateRow(rowId, {
        impType: impound.impoundType,
        impGroup: impound.impoundGroup,
        parcelId: impound.parcelNo || '',
        assessorCode: impound.assessorCode || '',
        unitNo: impound.unitNo || '',
        policyNo: impound.policyNo || impound.policyNum || '',
        renewalDate: impound.renewalDte,
      });
      const impoundMasterData = await getImpoundMasterData(impound);
      if (impoundMasterData) {
        const { standardTrCode, supplementalRaw, supplementalTrCode } =
          effectiveImpoundMasterTrCodes(impoundMasterData);
        setAvailableTransactions(
          props.transactionQidList.filter(
            (row: QidType) =>
              standardTrCode === row.code || supplementalRaw === row.code || supplementalTrCode === row.code,
          ),
        );
        if (trType === 'Supplemental') {
          updateField('mcdTransType', supplementalTrCode);
        } else {
          updateField('mcdTransType', standardTrCode);
        }
      }
    };

    if (IMPOUND_TRANSACTIONS.includes(props.selectedTransType)) {
      if (selectedImpound) {
        updateImpoundSelection(selectedImpound);
        setDisableTrType(false);
      } else {
        console.log('no impound selected');
        setAvailableTransactions([]);
        setDisableTrType(true);
      }
    }
  }, [
    selectedImpound,
    getImpoundMasterData,
    props.selectedTransType,
    props.transactionQidList,
    updateRow,
    rowId,
    trType,
  ]);

  // set the correct impound in the dropdown based on the data saved in the row
  useEffect(() => {
    if (servArray || parcels) {
      if (props.selectedTransType === 'HZ') {
        setSelectedImpound(
          servArray.find(
            (imp) =>
              imp.renewalDte === props.row.renewalDate &&
              imp.impoundType === props.row.impType &&
              imp.policyNo === props.row.policyNo,
          ),
        );
      } else if (isParcelDisbursementBatchType(props.selectedTransType)) {
        if (isHoaOrGroundRentParcelBatch(props.selectedTransType)) {
          setSelectedImpound(
            parcels.find(
              (imp) =>
                imp.renewalDte === props.row.renewalDate &&
                imp.impoundType === props.row.impType &&
                (imp.parcelNo === props.row.parcelId || imp.policyNum === props.row.policyNo),
            ),
          );
        } else {
          setSelectedImpound(
            parcels.find(
              (imp) =>
                imp.renewalDte === props.row.renewalDate &&
                imp.impoundType === props.row.impType &&
                imp.parcelNo === props.row.parcelId &&
                imp.assessorCode === props.row.assessorCode,
            ),
          );
        }
      } else if (props.selectedTransType === 'MI') {
        setSelectedImpound(
          servArray.find((imp) => imp.renewalDte === props.row.renewalDate && imp.impoundType === props.row.impType),
        );
      }
    }
  }, [
    parcels,
    servArray,
    props.row.assessorCode,
    props.row.renewalDate,
    props.row.impType,
    props.row.parcelId,
    props.row.policyNo,
    props.selectedTransType,
  ]);

  // set the available TR types when applicable
  useEffect(() => {
    if (TR_TYPE_TRANSACTIONS.includes(props.selectedTransType)) {
      setAvailableTrTypes(['Renewal', 'Supplemental', 'Refund', 'Exception']);
    }
  }, [props.selectedTransType]);

  // since the TR type is not saved in the backend, update based on the suffix which is saved
  useEffect(() => {
    if (TR_TYPE_TRANSACTIONS.includes(props.selectedTransType)) {
      if (props.row.mcdTransSuffix === '') {
        setTrType('Renewal');
      } else if (props.row.mcdTransSuffix === 'B') {
        setTrType('Supplemental');
      } else if (props.row.mcdTransSuffix === 'N') {
        setTrType('Refund');
      } else if (props.row.mcdTransSuffix === 'E') {
        setTrType('Exception');
      }
    }
  }, [props.row.mcdTransSuffix, props.selectedTransType]);

  const handleTrTypeChange = async (e) => {
    const newType = e.target.value;
    const impoundMasterData = await getImpoundMasterData(selectedImpound);
    let standardTrCode = '';
    let supplementalTrCode = '';
    if (impoundMasterData) {
      const codes = effectiveImpoundMasterTrCodes(impoundMasterData);
      standardTrCode = codes.standardTrCode;
      supplementalTrCode = codes.supplementalTrCode;
    }

    if (newType === 'Renewal') {
      updateField('mcdTransSuffix', '');
      updateField('mcdTransType', standardTrCode);
      setPaymentPositiveValue(true);
    } else if (newType === 'Supplemental') {
      updateField('mcdTransSuffix', 'B');
      updateField('mcdTransType', supplementalTrCode);
      setPaymentPositiveValue(true);
    } else if (newType === 'Exception') {
      updateField('mcdTransSuffix', 'E');
      updateField('mcdTransType', standardTrCode);
      setPaymentPositiveValue(true);
    } else if (newType === 'Refund') {
      updateField('mcdTransSuffix', 'N');
      updateField('mcdTransType', standardTrCode);
      setPaymentPositiveValue(false);
    }
  };

  // handle special transaction codes w/ multiple descriptions
  useEffect(() => {
    const transType = props.row.mcdTransType;
    if (transType) {
      if (MULTIPLE_DESCRIPTION_TRANSACTIONS.includes(transType)) {
        if (transType === '19') {
          setDescriptionOptions(['Assess Assessment Fee', 'Waive Assessment Fee']);
        } else if (transType === '26') {
          setDescriptionOptions([
            'Pay Corporate Advance',
            'Waive Corporate Advance',
            'Agency Pay Corporate',
            'Post Petition Corp Adv',
            'Pre Petition Corp Adv',
          ]);
        } else if (transType === '28') {
          setDescriptionOptions(['Assess LC', 'Waive Fees']);
        } else if (transType === '84') {
          setDescriptionOptions([
            'Reg Sus Ref',
            'Payment Ref',
            'Excess Ref',
            'Sus Ref',
            'LC Refund',
            'Fee Refund',
            'Ref PIF Sus',
          ]);
        } else if (transType === '86') {
          setDescriptionOptions([
            'Oth Sus Ref',
            'Payment Ref',
            'Excess Ref',
            'Sus Ref',
            'LC Refund',
            'Fee Refund',
            'Claim Refund',
            'Ref PIF Sus',
          ]);
        } else if (transType === '88') {
          setDescriptionOptions([
            'Assess Corporate Advance',
            'Non-Recoverable Fee',
            'Corporate Advance - Exception',
            'Post Petition Corp Adv',
            'Pre Petition Corp Adv',
          ]);
        } else if (transType === '13') {
          setDescriptionOptions([
            'Escrow Only',
            'Add Escrow',
            'Ins Refund',
            'Tax Refund',
            'Int On Esc',
            'Int on Loss Draft',
          ]);
        } else if (transType === '22') {
          const invoiceSuspenseDescription =
            props.transactionQidList.find((item) => item.code === '22')?.description ?? 'Invoice Suspense';
          setDescriptionOptions([invoiceSuspenseDescription, 'INT ON LDF']);
        } else if (transType === '80') {
          setDescriptionOptions([
            'Esc Refund',
            'Dup Bill Fee',
            'Ground Rent',
            'Sewer Lien',
            'Tax Lien',
            'Ref PIF Esc',
            'Esc Surplus',
            'Serv Rels',
            'INS Refund',
            'PMI Refund',
            'Tax Refund',
          ]);
        } else if (transType === '8H') {
          setDescriptionOptions(['HOME Repair', 'Choice reno']);
        } else if (transType === '2H') {
          setDescriptionOptions(['HOME Repair', 'Choice Reno']);
        } else {
          setDescriptionOptions([props.transactionQidList.find((item) => item.code === transType)?.description]);
        }
      }
    }
  }, [props.row.mcdTransType, props.suffixQidList, props.transactionQidList]);

  // set loan error
  useEffect(() => {
    if (props.row.loan === '') {
      if (!loadingLoan) {
        if (loan === null || loan === undefined || loanErrorMessages !== '') {
          setLoanError(true);
        } else {
          setLoanError(false);
        }
      }
    } else {
      setLoanError(false);
    }
  }, [loan, loanErrorMessages, loadingLoan, props.row.loan]);

  // set the available transaction codes based on transaction type selected by the user
  useEffect(() => {
    if (props.selectedTransType === 'MP') {
      setAvailableTransactions(
        props.transactionQidList.filter((row: QidType) => LOAN_PAYMENT_CODES.includes(row.code)),
      );
      setDisableTransactionCode(false);
    } else if (props.selectedTransType === 'MI') {
      setDisableTransactionCode(false);
    } else {
      setAvailableTransactions(props.transactionQidList);
      setDisableTransactionCode(true);
    }
  }, [props.selectedTransType, props.transactionQidList]);

  // set available suffixes based on transaction type
  useEffect(() => {
    let availableSuffixes = [];
    if (props.row.mcdTransType === '88') {
      availableSuffixes = ['E', 'O', 'R', 'X'];
    } else if (props.row.mcdTransType === '19') {
      availableSuffixes = ['X'];
    } else if (props.row.mcdTransType === '20') {
      availableSuffixes = ['R', 'O'];
    } else if (props.row.mcdTransType === '21') {
      availableSuffixes = ['X'];
    } else if (props.row.mcdTransType === '26') {
      availableSuffixes = ['I', 'X', 'R', 'O'];
    } else if (props.row.mcdTransType === '28') {
      availableSuffixes = ['X'];
    } else if (isParcelDisbursementBatchType(props.selectedTransType)) {
      availableSuffixes = ['B', 'E', 'N'];
    } else if (props.selectedTransType === 'HZ') {
      availableSuffixes = ['B', 'E', 'N'];
    } else if (props.selectedTransType === 'MI') {
      availableSuffixes = ['E', 'N'];
    } else if (['80', '82', '84', '86'].includes(props.selectedTransType)) {
      availableSuffixes = ['E'];
    } else if (['8H', '8B', '8C', '8K'].includes(props.selectedTransType)) {
      availableSuffixes = ['X'];
    }
    setSuffixList(
      availableSuffixes.length > 0
        ? props.suffixQidList.filter((item) => availableSuffixes.includes(item.code))
        : props.suffixQidList,
    );
  }, [props.row.mcdTransType, props.selectedTransType, props.suffixQidList]);

  // default field logic for transactions
  useEffect(() => {
    if (props.row.mcdTransType === '21' && !props.row.cnt) {
      updateField('mcdTransSuffix', 'X');
    }
    if (props.selectedTransType === 'HZ') {
      setDisableSuffix(true);
    }
    if (isParcelDisbursementBatchType(props.selectedTransType)) {
      setDisableSuffix(true);
    }
  }, [props.row.mcdTransType, updateField, props.selectedTransType]);

  // MI refunds use suffix N; flip amount sign like HZ/Tax Refund (TR_TYPE_TRANSACTIONS).
  useEffect(() => {
    if (props.selectedTransType === 'MI') {
      setPaymentPositiveValue(props.row.mcdTransSuffix !== 'N');
    }
  }, [props.selectedTransType, props.row.mcdTransSuffix]);

  // handle field logic for specific transactions
  useEffect(() => {
    if (props.row.mcdTransType === '88') {
      setFeeDescriptions(corpAdvanceFeeNumbers);
      setDisableSuffix(true);
      if (props.row.description === 'Non-Recoverable Fee') {
        updateField('mcdTransSuffix', 'X');
        setPaymentPositiveValue(false);
        setDisableFeeCnt(false);
        setDisableFeeNo(false);
        setFeeNoRequired(true);
        setFeeCntRequired(true);
      } else if (props.row.description === 'Assess Corporate Advance') {
        updateField('mcdTransSuffix', '');
        updateField('feeCnt', '');
        setPaymentPositiveValue(true);
        setDisableFeeNo(false);
        setDisableFeeCnt(true);
        setFeeNoRequired(true);
        setFeeCntRequired(false);
      } else if (props.row.description === 'Corporate Advance - Exception') {
        updateField('mcdTransSuffix', 'E');
        updateField('feeCnt', '');
        setPaymentPositiveValue(true);
        setDisableFeeNo(false);
        setDisableFeeCnt(true);
        setFeeNoRequired(true);
        setFeeCntRequired(false);
      } else if (props.row.description === 'Post Petition Corp Adv') {
        updateField('mcdTransSuffix', 'O');
        updateField('feeCnt', '');
        setPaymentPositiveValue(true);
        setDisableFeeNo(false);
        setDisableFeeCnt(true);
        setFeeNoRequired(true);
        setFeeCntRequired(false);
      } else if (props.row.description === 'Pre Petition Corp Adv') {
        updateField('mcdTransSuffix', 'R');
        updateField('feeCnt', '');
        setPaymentPositiveValue(true);
        setDisableFeeNo(false);
        setDisableFeeCnt(true);
        setFeeNoRequired(true);
        setFeeCntRequired(false);
      }
    } else if (props.row.mcdTransType === '19') {
      setFeeDescriptions(assessmentFeeNumbers);
      if (props.row.description === 'Assess Assessment Fee') {
        updateField('feeCnt', '');
        setDisableFeeCnt(true);
        setPaymentPositiveValue(true);
        setFeeNoRequired(true);
      } else if (props.row.description === 'Waive Assessment Fee') {
        setDisableFeeCnt(false);
        setPaymentPositiveValue(false);
        setFeeNoRequired(true);
      }
      updateField('mcdTransSuffix', 'X');
      setDisableSuffix(true);
    } else if (props.row.mcdTransType === '28') {
      updateField('mcdTransSuffix', 'X');
      setDisableSuffix(true);
      if (props.row.description === 'Waive Fees') {
        setPaymentPositiveValue(true);
      } else if (props.row.description === 'Assess LC') {
        setPaymentPositiveValue(false);
      }
    } else if (props.row.mcdTransType === '26') {
      setFeeDescriptions(corpAdvanceFeeNumbers);
      setDisableSuffix(true);
      if (props.row.description === 'Pay Corporate Advance') {
        updateField('mcdTransSuffix', '');
        setFeeNoRequired(false);
      } else if (props.row.description === 'Waive Corporate Advance') {
        updateField('mcdTransSuffix', 'X');
        setFeeNoRequired(true);
      } else if (props.row.description === 'Agency Pay Corporate') {
        updateField('mcdTransSuffix', 'I');
        setFeeNoRequired(false);
      } else if (props.row.description === 'Post Petition Corp Adv') {
        updateField('mcdTransSuffix', 'O');
        setFeeNoRequired(true);
      } else if (props.row.description === 'Pre Petition Corp Adv') {
        updateField('mcdTransSuffix', 'R');
        setFeeNoRequired(true);
      }
    } else if (props.row.mcdTransType === '20') {
      setFeeDescriptions(assessmentFeeNumbers);
    } else if (props.row.mcdTransType === '80') {
      if (PIF_FORECLOSURE_STATUSES.includes(loan.servStatus)) {
        setDisableSuffix(false);
      } else {
        setDisableSuffix(true);
        if (props.row.description === 'Ref PIF Esc') {
          updateField('mcdTransSuffix', 'E');
        } else {
          updateField('mcdTransSuffix', '');
        }
      }
    } else if (props.row.mcdTransType === '82') {
      if (PIF_FORECLOSURE_STATUSES.includes(loan.servStatus)) {
        setDisableSuffix(false);
      } else {
        setDisableSuffix(true);
        updateField('mcdTransSuffix', '');
      }
    } else if (props.row.mcdTransType === '84') {
      if (PIF_FORECLOSURE_STATUSES.includes(loan.servStatus)) {
        setDisableSuffix(false);
      } else {
        setDisableSuffix(true);
        if (props.row.description === 'Ref PIF Sus') {
          updateField('mcdTransSuffix', 'E');
        } else {
          updateField('mcdTransSuffix', '');
        }
      }
    } else if (props.row.mcdTransType === '86') {
      if (PIF_FORECLOSURE_STATUSES.includes(loan.servStatus)) {
        setDisableSuffix(false);
      } else {
        setDisableSuffix(true);
        if (props.row.description === 'Ref PIF Sus') {
          updateField('mcdTransSuffix', 'E');
        } else {
          updateField('mcdTransSuffix', '');
        }
      }
    }
  }, [
    loan.servStatus,
    props.row.mcdTransType,
    props.row.mcdTransSuffix,
    props.row.description,
    corpAdvanceFeeNumbers,
    assessmentFeeNumbers,
    updateField,
  ]);

  // update the payment amount to match the expected sign
  useEffect(() => {
    if (
      !allowNegativeAmount() &&
      ((paymentPositiveValue && parseFloat(props.row.amount) <= 0) ||
        (!paymentPositiveValue && parseFloat(props.row.amount) >= 0))
    ) {
      updateField('amount', parseFloat(props.row.amount) * -1);
    }
  }, [props.row.amount, paymentPositiveValue, props.row.mcdTransType, allowNegativeAmount]);

  // disable mortgage payment fields for all non-mortgage payment transactions in Multiple Payments
  useEffect(() => {
    if (props.selectedTransType === 'MP') {
      if (props.row.mcdTransType && (props.row.mcdTransType === '05' || props.row.mcdTransType === '08')) {
        setDisablePrincipalCurtail(false);
        setDisableEscrow(false);
        setDisableLateCharges(false);
        setDisableCorporateAdvance(false);
        setDisableAssessment(false);
        setDisableSuspense(false);
      } else {
        setDisablePrincipalCurtail(true);
        updateField('prinCurtailReqstd', 0);
        setDisableEscrow(true);
        updateField('extraEscrowReqstd', 0);
        setDisableLateCharges(true);
        updateField('lateChargeReqstd', 0);
        setDisableCorporateAdvance(true);
        updateField('corpAdvanceReqstd', 0);
        setDisableAssessment(true);
        updateField('assessmentsReqstd', 0);
        setDisableSuspense(true);
        updateField('suspenseReqstd', 0);
      }
    }
  }, [props.selectedTransType, props.row.mcdTransType]);

  // update the amount field when the row's total changes for transactions where each row's total is displayed inline
  useEffect(() => {
    if (ROW_TOTAL_TRANSACTIONS.includes(props.selectedTransType)) {
      updateField('amount', props.row.total);
    }
  }, [props.row.total, props.selectedTransType, updateField]);

  // get the reason from the loan and update if loan number changes
  useEffect(() => {
    if (REASON_TRANSACTIONS.includes(props.row.mcdTransType)) {
      updateField('reason', loan.PIFReason);
    }
  }, [loan, updateField, props.row.mcdTransType]);

  // for transactions that allow funds to be drawn from suspense, require suspense to be negative
  useEffect(() => {
    if (
      SUSPENSE_TRANSACTIONS.includes(props.row.mcdTransType) &&
      PAYMENT_AMOUNT_TRANSACTIONS.includes(props.row.mcdTransType) &&
      !MORTGAGE_PAYMENT_TRANSACTIONS.includes(props.row.mcdTransType)
    ) {
      if (parseFloat(props.row.suspenseReqstd) > 0) {
        updateField('suspenseReqstd', parseFloat(props.row.suspenseReqstd) * -1);
      }
    }
  }, [updateField, props.row.mcdTransType, props.row.suspenseReqstd]);

  // When the user changes the Investor Due Date field, refetch the expected UPB for the new date
  useEffect(() => {
    const fetchExpectedUpb = async () => {
      if (!props.token || !props.row.loan || props.row.loan.length !== 12) return;
      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };
      const controller = get();
      const config = { signal: controller.signal };
      const ownerTransferDateAsString = formatSecondsTimestampAsDateInput(props.row.ownerXferDate).replaceAll('-', '');

      try {
        const url = 'live/servicing/loan/' + props.row.loan + '/expectedUpb/' + ownerTransferDateAsString;

        const response = await axiosCall({ url: url, method: 'get' }, config, headers);

        if (response) {
          setExpectedUpb(response.expectedUpb);
        }
      } catch (error) {
        console.error('Error fetching expected UPB for: ' + ownerTransferDateAsString, error);
      }
    };

    if (props.row.ownerXferDate) {
      fetchExpectedUpb();
    }
  }, [props.token, props.row.loan, props.row.ownerXferDate]);

  const isRowActionLocked = props.postingRowId != null;

  return (
    <ClickAwayListener tagName='tr'>
      <>
        <TableCell align='center' className={Style.utilityCell}>
          <div className={Style.rowActions}>
            <Tooltip
              title={
                transactionSaveErrors.length > 0 || props.row.errors
                  ? 'Cannot post — fix errors shown in status column'
                  : 'Post this transaction'
              }
            >
              <span>
                <Button
                  variant='contained'
                  size='small'
                  className={Style.rowPostButton}
                  disabled={
                    isRowActionLocked ||
                    transactionSaveErrors.length > 0 ||
                    Boolean(props.row.errors) ||
                    loadingLoan
                  }
                  onClick={() => props.onPostRow(props.row)}
                  aria-label='Post this transaction'
                >
                  POST
                </Button>
              </span>
            </Tooltip>
            <Tooltip title='Delete this transaction'>
              <span>
                <IconButton
                  size='small'
                  disabled={isRowActionLocked}
                  onClick={() => {
                    props.removeRow(props.row.total, props.row.cnt, props.row.id);
                  }}
                  aria-label='Delete this transaction'
                >
                  <Delete className={Style.icon} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </TableCell>
        <TableCell align='center' className={Style.utilityCell}>
          {transactionSaveErrors.length > 0 || transactionPostErrors.length > 0 || props.row.errors ? (
            <Tooltip
              placement='right'
              title={
                <Fragment>
                  {transactionSaveErrors.length > 0 ? (
                    <Fragment>
                      <Typography>Transaction cannot be saved for loan: {props.row.loan}</Typography>
                      <ul>
                        {transactionSaveErrors.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </Fragment>
                  ) : (
                    <></>
                  )}
                  {transactionPostErrors.length > 0 ? (
                    <Fragment>
                      <Typography>Transaction cannot be posted for loan: {props.row.loan}</Typography>
                      <ul>
                        {transactionPostErrors.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </Fragment>
                  ) : (
                    <></>
                  )}
                  {props.row.errors ? (
                    <Fragment>
                      <Typography>Errors from trying to save/post transaction:</Typography>
                      <ul>
                        <li>{props.row.errors}</li>
                      </ul>
                    </Fragment>
                  ) : (
                    <></>
                  )}
                </Fragment>
              }
            >
              <ErrorOutlineTwoTone className={Style.errorIcon} />
            </Tooltip>
          ) : (
            <Tooltip
              placement='right'
              title={
                <Fragment>
                  <Typography>Loan Number is valid: {props.row.loan}</Typography>
                </Fragment>
              }
            >
              <CheckCircleOutlineTwoTone className={Style.successIcon} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell align='center' className={`${Style.utilityCell} ${Style.utilityCellLast}`}>
          {props.index + 1}
        </TableCell>
        {PAYMENT_RECEIVED_DATE_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={Boolean(props.disablePaymentReceivedDate)}
                value={dayjs(props.row.dateReceived * 1000)}
                onChange={(newDate) => {
                  if (newDate?.isValid()) {
                    updateField('dateReceived', newDate.unix());
                  } else {
                    updateField('dateReceived', null);
                  }
                }}
                slotProps={{
                  textField: {
                    variant: 'standard',
                    margin: 'none',
                    sx: { width: '100%' },
                  },
                }}
              />
            </LocalizationProvider>
          </TableCell>
        ) : (
          <></>
        )}
        <TableCell align='center' className={Style.cell}>
          <TextField
            error={loanError}
            helperText={loanError ? 'Verify Loan Number.' : ''}
            variant='standard'
            value={tempLoanNumber}
            sx={{ width: '100%' }}
            onBlur={() => updateField('loan', tempLoanNumber)}
            onChange={(e) => setTempLoanNumber(e.target.value)}
            disabled={Boolean(props.row.cnt)}
          />
        </TableCell>
        {IMPOUND_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            {props.selectedTransType === 'HZ' ? (
              <Select value={selectedImpound ? selectedImpound.index : ''} onChange={handleSelectImpoundChange}>
                <MenuItem key='blank' value=''>
                  {''}
                </MenuItem>
                {servArray.map((item, index) => (
                  <MenuItem key={index} value={item.index}>
                    <div>{item.impoundType}&nbsp;</div>
                    <div>{getDateValue(item.renewalDte)}&nbsp;</div>
                    <div>${item.amountDue}&nbsp;</div>
                    <div>{item.policyNo}&nbsp;</div>
                  </MenuItem>
                ))}
              </Select>
            ) : props.selectedTransType === 'MI' ? (
              <Select value={selectedImpound ? selectedImpound.index : ''} onChange={handleSelectImpoundChange}>
                <MenuItem key='blank' value=''>
                  {''}
                </MenuItem>
                {servArray.map((item, index) => (
                  <MenuItem key={index} value={item.index}>
                    <div>{item.impoundType}&nbsp;</div>
                    <div>{getDateValue(item.renewalDte)}&nbsp;</div>
                    <div>${item.amountDue}&nbsp;</div>
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select value={selectedImpound ? selectedImpound.index : ''} onChange={handleSelectTaxImpoundChange}>
                <MenuItem key='blank' value=''>
                  {''}
                </MenuItem>
                {parcels.map((parcel, index) =>
                  isHoaOrGroundRentParcelBatch(props.selectedTransType) ? (
                    <MenuItem key={index} value={parcel.index}>
                      <div>{parcel.impoundType}&nbsp;</div>
                      <div>{formatHoaGrPayeeLine(parcel, hoaGrInsCompanies, hoaGrMiscPayees)}&nbsp;</div>
                      <div>{getDateValue(parcel.renewalDte)}&nbsp;</div>
                      <div>${parcel.taxAmountDue}&nbsp;</div>
                      <div>${parcel.servSupPremium ?? 0}&nbsp;</div>
                      <div>{impoundPaymentStatusLabel(parcel)}&nbsp;</div>
                      <div>{parcel.policyNum}&nbsp;</div>
                    </MenuItem>
                  ) : (
                    <MenuItem key={index} value={parcel.index}>
                      <div>{parcel.impoundType}&nbsp;</div>
                      <div>{getDateValue(parcel.renewalDte)}&nbsp;</div>
                      <div>${parcel.taxAmountDue}&nbsp;</div>
                      <div>{parcel.parcelNo}&nbsp;</div>
                      <div>
                        {parcel.nameOne && parcel.nameOne !== parcel.assessorCode
                          ? `${parcel.assessorCode} - ${parcel.nameOne}`
                          : parcel.assessorCode}
                        &nbsp;
                      </div>
                    </MenuItem>
                  ),
                )}
              </Select>
            )}
          </TableCell>
        ) : (
          <></>
        )}
        {TR_TYPE_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Select value={trType} onChange={(e) => handleTrTypeChange(e)} disabled={disableTrType}>
              {availableTrTypes.map((item, index) => (
                <MenuItem key={index} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </TableCell>
        ) : (
          <></>
        )}
        <TableCell align='center' className={Style.cell}>
          <FormControl variant='standard' sx={{ width: '100%' }}>
            <Select
              value={props.row.mcdTransType}
              onChange={(e) => updateField('mcdTransType', e.target.value)}
              disabled={disableTransactionCode}
            >
              <MenuItem key='blank' value=''>
                {''}
              </MenuItem>
              {availableTransactions.map((r: QidType, index: number) => (
                <MenuItem key={index} value={r.code}>
                  {r.code} - {r.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        {SUFFIX_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <FormControl variant='standard' sx={{ width: '100%' }}>
              <Select
                value={props.row.mcdTransSuffix}
                onChange={(e) => updateField('mcdTransSuffix', e.target.value)}
                disabled={disableSuffix}
              >
                <MenuItem key='blank' value=''>
                  {''}
                </MenuItem>
                {suffixList.map((s, index: number) => (
                  <MenuItem key={index} value={s.code}>
                    {s.code} - {s.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </TableCell>
        ) : (
          <></>
        )}
        {MULTIPLE_DESCRIPTION_TRANSACTIONS.includes(props.row.mcdTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Select value={props.row.description} onChange={(e) => updateField('description', e.target.value)}>
              {descriptionOptions.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </TableCell>
        ) : (
          <TableCell align='center' className={Style.cell}>
            <Input
              value={props.transactionQidList.find((item) => item.code === props.row.mcdTransType)?.description || ''}
              sx={{ width: '100%' }}
              disabled
            />
          </TableCell>
        )}
        {SUBSIDY_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Select
              value={props.row.subsidyCnt}
              onChange={(e) => handleSubsidyChange(e.target.value)}
              disabled={props.row.loan.length !== 12 || loadingSubsidies}
              displayEmpty
            >
              <MenuItem key='blank' value=''>
                {loadingSubsidies ? 'Loading...' : 'Select Subsidy'}
              </MenuItem>
              {subsidies.map((subsidy) => (
                <MenuItem key={subsidy.cnt} value={subsidy.cnt}>
                  {subsidy.subsidyName} ({subsidy.cnt}) - {formatMoney(subsidy.monthlyAmount)}/mo -{' '}
                  {getDateValue(subsidy.startDate)} to {getDateValue(subsidy.endDate)}
                </MenuItem>
              ))}
            </Select>
            {suggestedSubsidyAmount != null ? (
              <Typography variant='caption' display='block'>
                Suggested: {formatMoney(suggestedSubsidyAmount)}
              </Typography>
            ) : null}
          </TableCell>
        ) : (
          <></>
        )}
        {CHECK_NUMBER_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Input
              value={props.row.checkNo}
              sx={{ width: '100%' }}
              onChange={(e) => updateField('checkNo', e.target.value)}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {MORTGAGE_PAYMENT_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={mortgagePayment}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              disabled
            />
          </TableCell>
        ) : (
          <></>
        )}
        {PAYMENT_AMOUNT_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.amount}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              thousandSeparator={true}
              onValueChange={(e) => updatePaymentAmount(e)}
              className={Style.numberFormat}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {PRINCIPAL_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.principalReqstd}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              onChange={(e) => updateField('principalReqstd', e.target.value.replace(/,/g, '').replace('$', ''))}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {INTEREST_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.interestReqstd}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              onChange={(e) => updateField('interestReqstd', e.target.value.replace(/,/g, '').replace('$', ''))}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {EXCESS_FUNDS_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={excessFunds}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale={true}
              thousandSeparator={true}
              className={Style.numberFormat}
              disabled
            />
          </TableCell>
        ) : (
          <></>
        )}
        {SUSPENSE_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.suspenseReqstd}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              onChange={(e) => updateField('suspenseReqstd', e.target.value.replace(/,/g, '').replace('$', ''))}
              disabled={disableSuspense}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {PRINCIPAL_CURTAIL_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.prinCurtailReqstd}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              onChange={(e) => updateField('prinCurtailReqstd', e.target.value.replace(/,/g, '').replace('$', ''))}
              disabled={disablePrincipalCurtail}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {ESCROW_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.extraEscrowReqstd}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              onChange={(e) => updateField('extraEscrowReqstd', e.target.value.replace(/,/g, '').replace('$', ''))}
              disabled={disableEscrow}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {LATE_CHARGE_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.lateChargeReqstd}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              onChange={(e) => updateField('lateChargeReqstd', e.target.value.replace(/,/g, '').replace('$', ''))}
              disabled={disableLateCharges}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {CORPORATE_ADVANCE_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.corpAdvanceReqstd}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              onChange={(e) => updateField('corpAdvanceReqstd', e.target.value.replace(/,/g, '').replace('$', ''))}
              disabled={disableCorporateAdvance}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {ASSESSMENT_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.assessmentsReqstd}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              onChange={(e) => updateField('assessmentsReqstd', e.target.value.replace(/,/g, '').replace('$', ''))}
              disabled={disableAssessment}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {ROW_TOTAL_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <NumericFormat
              value={props.row.total}
              className={Style.numberFormat}
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              type='text'
              thousandsGroupStyle='thousand'
              thousandSeparator=','
              onChange={(e) => updatePaymentAmount(e)}
              disabled
            />
          </TableCell>
        ) : (
          <></>
        )}
        {UNIT_NO_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Input
              value={props.row.unitNo}
              sx={{ width: '100%' }}
              onChange={(e) => updateField('unitNo', e.target.value)}
              disabled
            />
          </TableCell>
        ) : (
          <></>
        )}
        {FEE_NO_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Select
              value={props.row.feeNo}
              onChange={(e) => updateField('feeNo', e.target.value)}
              disabled={disableFeeNo}
            >
              {feeNoRequired ? (
                <></>
              ) : (
                <MenuItem key='blank' value=''>
                  {''}
                </MenuItem>
              )}
              {feeDescriptions.map((option, index) => (
                <MenuItem key={index} value={option.feeNo}>
                  {option.feeNo} - {option.description}
                </MenuItem>
              ))}
            </Select>
          </TableCell>
        ) : (
          <></>
        )}
        {FEE_CNT_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Select
              value={props.row.feeCnt}
              onChange={(e) => updateField('feeCnt', e.target.value)}
              disabled={disableFeeCnt}
            >
              {feeCntRequired ? (
                <></>
              ) : (
                <MenuItem key='blank' value=''>
                  {''}
                </MenuItem>
              )}
              {servicingFees.map((option, index) => (
                <MenuItem key={index} value={option.count}>
                  {getDateValue(option.dateAssessed)} - ${option.servicingBalance}
                </MenuItem>
              ))}
            </Select>
          </TableCell>
        ) : (
          <></>
        )}
        {REASON_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Select value={props.row.reason || ''} onChange={(e) => updateField('reason', e.target.value)}>
              <MenuItem key='blank' value=''>
                {'None'}
              </MenuItem>
              {reasonList.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.code} - {option.description}
                </MenuItem>
              ))}
            </Select>
          </TableCell>
        ) : (
          <></>
        )}
        {PAYEE_TRANSACTIONS.includes(props.selectedTransType) ? (
          isParcelDisbursementBatchType(props.selectedTransType) ? (
            <TableCell align='center' className={Style.cell}>
              <Input
                value={
                  isHoaOrGroundRentParcelBatch(props.selectedTransType) ? parcelLinePayeeDisplay : payeeInfo.nameOne
                }
                sx={{ width: '100%' }}
                disabled
              />
            </TableCell>
          ) : (
            <TableCell align='center' className={Style.cell}>
              <Input
                value={props.row.payee}
                sx={{ width: '100%' }}
                onChange={(e) => updateField('payee', e.target.value)}
              />
            </TableCell>
          )
        ) : (
          <></>
        )}
        {PAYEE2_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Input
              value={props.row.payeeTwo}
              sx={{ width: '100%' }}
              onChange={(e) => updateField('payeeTwo', e.target.value)}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {PAYEE3_TRANSACTIONS.includes(props.selectedTransType) ? (
          <TableCell align='center' className={Style.cell}>
            <Input
              value={props.row.payeeThree}
              sx={{ width: '100%' }}
              onChange={(e) => updateField('payeeThree', e.target.value)}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {SALE_TRANSFER_TRANSACTIONS.includes(props.selectedTransType) ? (
          <>
            <TableCell align='center' className={Style.cell}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={props.row.ownerXferDate ? dayjs(props.row.ownerXferDate * 1000) : null}
                  format='MM/DD/YYYY'
                  onChange={(newDate) => {
                    if (newDate?.isValid()) {
                      updateField('ownerXferDate', newDate.unix());
                    } else {
                      updateField('ownerXferDate', null);
                    }
                  }}
                />
              </LocalizationProvider>
            </TableCell>
            <TableCell align='center' className={Style.cell}>
              <NumericFormat
                value={expectedUpb}
                className={Style.numberFormat}
                prefix={'$'}
                decimalScale={2}
                fixedDecimalScale
                type='text'
                thousandsGroupStyle='thousand'
                thousandSeparator=','
                disabled
              />
            </TableCell>
            <TableCell align='center' className={Style.cell}>
              <Input value={props.row.to} sx={{ width: '100%' }} onChange={(e) => updateField('to', e.target.value)} />
            </TableCell>
            <TableCell align='center' className={Style.cell}>
              <Input
                value={props.row.from}
                sx={{ width: '100%' }}
                onChange={(e) => updateField('from', e.target.value)}
              />
            </TableCell>
            <TableCell align='center' className={Style.cell}>
              <Input
                value={props.row.groupCode}
                sx={{ width: '100%' }}
                onChange={(e) => updateField('groupCode', e.target.value)}
              />
            </TableCell>
          </>
        ) : (
          <></>
        )}
      </>
    </ClickAwayListener>
  );
});

export default LoanPaymentRow;
